import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import NewLayout from "@components/new-ui/Layout";
import ProblemSectionCard, { ProblemSection } from "@components/solution/problemSectionCard";
import { processEnv } from "@lib/processEnv";
import { IIndustries } from "@pages/industry/finance/whatsapp";
import { windowOpenNewTab } from "@utils/common";
import axios from "axios";
import { GetStaticProps } from "next";
import Image from "next/image";
import React from "react";
import { MdOutlineArrowForwardIos } from "react-icons/md";

const featureInfo1: FeatureInfo = {
    mainTitle: "Dealership and services management hub",
    subtitle2: "Monitor every lead that comes into the system, schedule services, and track deliveries, all in one place. With Gallabox, you can:",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/automotive/management-hub.png",
    featureInfoList: [
        "Streamline lead nurturing and follow-ups",
        "Send and receive  documents  on WhatsApp",
        "Book test drives",
        "Schedule services",
        "Facilitate secure payments.",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Instant inquiry support and lead nurturing",
    subtitle2: "Deploy 24/7 support bots to address lead queries in real-time, and promote better customer relationships. You can use Gallabox’s chatbots to:",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/automotive/support-and-lead.png",
    featureInfoList: ["Address lead inquiries", "Nurture with quality promotional campaigns", "Automatically schedule services", "Collect payments", "Send feedback surveys."],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Ad integrations for better lead capturing",
    subtitle2: "Integrate your ads with WhatsApp to increase the ROI of your ad campaigns. With Gallabox as your CTWA software, you can:",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/automotive/lead-capture.png",
    featureInfoList: [
        "Direct users from your ads to WhatsApp for more tailored conversations",
        "Increase the chances of lead conversions",
        "Retarget with better intent signals",
        "Automatically enroll in personalized drip campaigns",
        "Set up automotive industry-specific query handling chatbots.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Effortless DMS integration ecosystem",
    subtitle2: "Connect WhatsApp to your dealership’s existing delivery management systems. With Gallabox’s 30+ integrations, you can:",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/automotive/integration.png",
    featureInfoList: [
        "Connect your DMS for a single source of truth on lead data",
        "Easily migrate information from Gallabox to your other tools",
        "Update data across your tech stack in real-time.",
    ],
};

const automotiveProductCardData: Array<Product> = [
    {
        title: "Better lead qualification and prospect nurturing",
        description: "By automating lead follow-ups, qualification, and test drive bookings, you’re saving over 80% in resource costs.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/pipleline.svg",
    },
    {
        title: "Intelligent automotive chatbots",
        description: "Drive better business outcomes and revenue by using chatbots to answer customer enquiries in real-time",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/bot.svg",
    },
    {
        title: "Centralized communication",
        description: "Use one single tool to push engagement and lead capturing, and keep all your data streamlined in a single place.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-chat.svg",
    },
    {
        title: "Scale performance, not budgets",
        description: "Keep costs at bay using a solution that minimises budget usage while keeping performance steady & growing.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/rocket.svg",
    },
    {
        title: "Seamless DMS integration",
        description: "Sync WhatsApp data with your existing dealership management system and maintain data sanity across your tech stack.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/integrate.svg",
    },
    {
        title: "Real-time actionable insights",
        description: "Monitor live engagement and conversion metrics, such as open rates, and response times to refine your outreach efforts.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/data-analysis.svg",
    },
    {
        title: "Shared team access for better support",
        description: "Assign roles (sales, support, marketing) with granular permissions, ensuring teams work cohesively.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/inbox-download.svg",
    },
    {
        title: "Built-in security compliance",
        description: "Gallabox is compliant with GDPR and SOC guidelines and makes sure your data is encrypted and kept safe at all times.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/secure-database.svg",
    },
];

const herosectionData: HerosectionProps = {
    chipTitle: "AUTOMOTIVE INDUSTRY",
    title: "Close deals faster with WhatsApp business solution for automotive",
    description:
        "With Gallabox as your WhatsApp Business solution for the automotive industry, drive better impact for your customers with seamless service bookings, instant product suggestions, inquiry support, and deal updates.",
    primaryButtonText: "Start free trial",
    imageUrl: "https://assets.gallabox.com/gb-home/industries/automotive/industry-automotive-hero.png",
};

const productCardData: ProblemSection = {
    maintitle: "Automotive dealerships struggle to make sales",
    problemSectionCard: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/personalisation.svg",
            title: "Manual lead management",
            description: "Running back and forth between enquiry calls, paper quotes, and catalogs can most definitely lead to confusion, and most importantly, lost sales.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/person-blockpng.png",
            title: "Too many dead-end test drives",
            description: "You only have so many agents — they can’t all be taking all your walk-ins to a test drive. This can cause burnout and churn across the board.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/filter.png",
            title: "Bad follow-up routines",
            description: "Most agents turn their backs on prospects that walk out. They might have been just one or two follow-up sequences away from a big purchase.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/campaign.svg",
            title: "Mistimed and badly scripted promotions",
            description: "This happens more often than you’d think — one mistimed promotional campaign and you’ve already lost a potentially good sale.",
        },
    ],
};

const WhatsappAutomotive: React.FC<IIndustries> = ({ categoryResult }) => {
    return (
        <NewLayout
            description="Make use of WhatsApp Business for automotive dealerships and drive better sales and conversions for your automotive business using Gallabox"
            title="WhatsApp Business Solutions for Automotives and Automobiles"
            canonical="https://gallabox.com/industry/automotive/whatsapp"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/industries/automotive/industry-automotive-og.png" }}
        >
            <Herosection herosection={herosectionData} />

            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>

            <ProblemSectionCard problemSection={productCardData} />
            <h2 className="md:text-6xl text-4xl font-semibold pb-10 max-w-7xl mx-auto  px-5" style={{ fontFamily: "Degular Display" }}>
                WhatsApp Business solution built for the automotive industry
            </h2>
            <FeatureInfoCard {...featureInfo1} />
            <FeatureInfoCard {...featureInfo2} />
            <FeatureInfoCard {...featureInfo3} />
            <FeatureInfoCard {...featureInfo4} />
            <ProductSection
                productCardData={automotiveProductCardData}
                title="Key benefits of integrating Gallabox into your Automotive tech stack"
                description="Improve response times. Increase deliveries. Delight customers."
            />

            <section className={`${container_classname} py-20`}>
                <h2 className="md:text-6xl text-4xl mb-12" style={{ fontFamily: "Degular Display" }}>
                    Ready-to-test automotve chatbot templates
                </h2>
                <div className={"grid md:grid-cols-3 md:gap-5 gap-10"}>
                    {categoryResult.slice(0, 3).map((data: any) => (
                        <div
                            key={data.id}
                            className="border border-solid border-gray-200 rounded cursor-pointer"
                            onClick={() => windowOpenNewTab(`/whatsapp-chatbot-templates/${data.category}/${data.title}/${data.id}`)}
                        >
                            <div className="relative">
                                <Image src={data.picture} alt={data.title} width={280} height={170} style={{ height: "200px" }} className="w-full" />

                                <div
                                    className="absolute top-0 left-0 w-full h-full"
                                    style={{
                                        background: "linear-gradient(180deg, rgba(51, 100, 250, 0.00) 0%, rgba(51, 100, 250, 0.12) 100%)",
                                    }}
                                ></div>
                            </div>
                            <div className="p-8 bg-white">
                                {data.tags?.length > 0 && (
                                    <p className="mb-4 text-gray-700 text-sm">{data.tags.map((tag: { name: string }) => tag.name.toUpperCase()).join(" / ")}</p>
                                )}
                                <div className="h-24">
                                    <p className="mb-12 text-2xl font-medium">{data.title}</p>
                                </div>
                                <div className="flex gap-x-4 items-center">
                                    <a
                                        href={`/try-template/${data.id}`}
                                        className="border border-solid border-blue-600 text-blue-600 rounded no-underline hover:no-underline text-sm"
                                        style={{ lineHeight: "14px", padding: "8px 12px 8px 12px" }}
                                    >
                                        TRY
                                    </a>
                                    <div className="flex gap-1 items-center">
                                        <Image src="/new-industries/education/view.svg" width={20} height={20} className="w-8 h-6" alt="visited" />
                                        {data.usedCount}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="mt-9 text-blue-500 flex justify-center">
                    <a href="https://gallabox.com/whatsapp-chatbot-templates/education" className="flex items-center gap-x-2">
                        View all <MdOutlineArrowForwardIos size={14} />
                    </a>
                </p>
            </section>

            <IntegrationSection isLight={true} />
            <AwardsSection />
            <CTASection title="Ready to Scale Your Logistics with WhatsApp?" primaryButtonText="Start free trial" />
        </NewLayout>
    );
};

export default WhatsappAutomotive;

export const getStaticProps: GetStaticProps = async () => {
    let categoryResult;

    try {
        await axios({
            method: "get",
            url: "https://server.gallabox.com/devapi/bot-templates",
            headers: {
                apiSecret: process.env.REACT_APP_API_SECRET,
                apiKey: process.env.REACT_APP_API_KEY,
            },
            data: {
                status: "published",
                category: "automobile",
            },
        })
            .then(function (response) {
                categoryResult = JSON.parse(JSON.stringify(response?.data)) || [];
            })
            .catch(function (err) {
                console.log(err);
            });
    } catch (error) {
        throw new Error("Index creation failed.");
    }

    return {
        props: { categoryResult },
        ...(processEnv.isr.enable && { revalidate: 1 }),
    };
};
